import type { ItemMaster } from "../api/types.ts"
import { useI18n } from "../i18n/index.tsx"
import {
  type IntakeLine,
  masterLabel,
  newIntakeLine,
} from "../lib/intake-lines.ts"
import { Button, Field, IconButton, Input, Select } from "../ui/index.ts"

/**
 * 무엇이 몇 개 돌아오는지 적는 칸.
 *
 * 품목 마스터에서 고르는 것이 기본이고, 목록에 없으면 직접 적는다. 고른 품명은 서버가
 * 마스터에서 복사해 두므로(스냅샷) 마스터를 나중에 고쳐도 이 접수 기록은 바뀌지 않는다.
 *
 * 무엇을 보낼 수 있는지는 `lib/intake-lines.ts` 가 정한다(테스트가 지킨다).
 */
export function IntakeLines({
  lines,
  masters,
  onChange,
}: {
  lines: IntakeLine[]
  masters: ItemMaster[]
  onChange: (lines: IntakeLine[]) => void
}) {
  const { t, locale } = useI18n()

  const set = (key: number, patch: Partial<IntakeLine>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)))

  return (
    <div className="lines">
      {lines.map((line, index) => (
        <div className="lines__row" key={line.key}>
          <Field label={index === 0 ? t("lines.item") : ""} htmlFor={`line-item-${line.key}`}>
            <Select
              id={`line-item-${line.key}`}
              value={line.itemId}
              onChange={(event) => set(line.key, { itemId: event.target.value })}
            >
              <option value="">{t("lines.manual")}</option>
              {masters.map((master) => (
                <option key={master.id} value={master.id}>
                  {masterLabel(master, locale)}
                </option>
              ))}
            </Select>
          </Field>

          {line.itemId === "" ? (
            <Field
              label={index === 0 ? t("lines.description") : ""}
              htmlFor={`line-desc-${line.key}`}
            >
              <Input
                id={`line-desc-${line.key}`}
                value={line.description}
                placeholder={t("lines.descriptionHint")}
                onChange={(event) => set(line.key, { description: event.target.value })}
              />
            </Field>
          ) : null}

          <Field label={index === 0 ? t("lines.qty") : ""} htmlFor={`line-qty-${line.key}`}>
            <Input
              id={`line-qty-${line.key}`}
              className="lines__qty"
              inputMode="numeric"
              value={line.qty}
              onChange={(event) =>
                set(line.key, { qty: event.target.value.replace(/\D/g, "") })
              }
            />
          </Field>

          {/* 한 줄은 남겨 둔다 — 상품 없는 접수는 받지 않는다. */}
          {lines.length > 1 ? (
            <IconButton
              icon="trash"
              label={t("lines.remove")}
              className="lines__remove"
              onClick={() => onChange(lines.filter((other) => other.key !== line.key))}
            />
          ) : null}
        </div>
      ))}

      <Button icon="plus" onClick={() => onChange([...lines, newIntakeLine()])}>
        {t("lines.add")}
      </Button>
    </div>
  )
}
